<template>
  <div
    id="coach-summary-section"
    class="i-coach-summary"
  >
    <div class="left">
      <img
        class="image"
        alt="Your coach's picture"
        :src="assignedCoach?.profilePhotoUrl || ''"
      />
      <div class="intro">
        <template v-if="!!assignedCoach?.fullName && !isDesktopOrLarger">
          <div class="top-pick">Top pick: {{ assignedCoach?.percentMatch }}% Match</div>
        </template>
        <div class="name">{{ assignedCoach?.fullName || "Your Coach" }}</div>
        <div>Career Coach</div>
        <div class="rating">
          <img
            alt="5 stars rating"
            :src="iconStars"
          />
          {{ assignedCoach?.rating }} stars
        </div>
      </div>
    </div>
    <div class="right">
      <div class="column">
        <div class="header">
          <template v-if="!!assignedCoach.matchReason">
            Why {{ assignedCoachFirstName || "Your Coach" }} is your ideal coach
          </template>
          <template v-else>{{ assignedCoachFirstName || "Your Coach" }}'s Biography</template>
          <template v-if="!!assignedCoach?.fullName && isDesktopOrLarger">
            <div class="top-pick">Top pick: {{ assignedCoach?.percentMatch }}% Match</div>
          </template>
        </div>
        <iBaseExpandableParagraph
          :text="assignedCoach.matchReason || assignedCoach?.biography || ''"
          :text-size="descriptionFontSize"
          :collapsed-length="descriptionMaxlength"
          :cta-color="azureRadiance500"
          :show-icon="false"
        />
      </div>
      <div class="column">
        <div class="header">{{ assignedCoach?.fullName || "Your Coach" }} will help with:</div>
        <div class="perks">
          <div
            v-for="item in LIST_ITEMS"
            :key="item"
            class="item"
          >
            <img :src="iconTick" /> {{ item }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useCoachingStore } from "@/stores/coaching"
import { azureRadiance500 } from "@/styles/colors"
import { breakpointLg, breakpointSm } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"

import iBaseExpandableParagraph from "@/components/base/BaseExpandableParagraph.vue"

// ASSETS
import iconTick from "@/assets/icons/icon-green-check.svg"
import iconStars from "@/assets/icons/icon-five-slashed-stars.svg"

// STORES AND COMPOSABLES
const coachingStore = useCoachingStore()
const isMobileOrLarger = useIsScreenSize(breakpointSm)
const isDesktopOrLarger = useIsScreenSize(breakpointLg)

// CONSTANTS
const LIST_ITEMS = [
  "Career Transition Support",
  "Resume, Cover Letter and LinkedIn Review",
  "Accountability Coaching",
  "Job Application Support",
  "Interview Preparation",
  "Salary Negotiations"
]

// COMPUTED
const assignedCoach = computed(() => coachingStore.getAssignedCoach)
const assignedCoachFirstName = computed(() => assignedCoach.value?.fullName?.split(" ")[0])
const descriptionFontSize = computed(() => (isMobileOrLarger.value ? "16px" : "14px"))
const descriptionMaxlength = computed(() => (isDesktopOrLarger.value ? 610 : isMobileOrLarger.value ? 480 : 380))
</script>

<style lang="scss" scoped>
.i-coach-summary {
  @include flex(column, start, stretch, $gap: 25px);

  scroll-margin-top: 25px;
  margin: auto;
  max-width: 1000px;
  padding: 0 20px;
  margin-top: 56px;
  margin-bottom: 40px;

  @media screen and (min-width: $breakpoint-md) {
    @include flex(row, center, start, $gap: 48px);
  }

  .top-pick {
    @include border-radius-xxl;

    font-size: 10px;
    width: fit-content;
    padding: 2px 10px;
    color: $white;
    text-transform: uppercase;
    font-weight: 500;
    background-image: $blue-purple-gradient;

    @media screen and (min-width: $breakpoint-xs) {
      @include text-xs;

      padding: 4px 12px;
    }

    @media screen and (min-width: $breakpoint-lg) {
      @include text-sm;

      font-weight: 400;
    }
  }

  .left {
    @include flex(row, start, center, $gap: 14px);

    @media screen and (min-width: $breakpoint-xs) {
      gap: 24px;
    }

    @media screen and (min-width: $breakpoint-md) {
      @include flex(column, start, start, $gap: 24px);
    }

    .image {
      @include border-radius-md;

      max-width: 110px;

      @media screen and (min-width: $breakpoint-xs) {
        max-width: 130px;
      }

      @media screen and (min-width: $breakpoint-sm) {
        max-width: 190px;
      }

      @media screen and (min-width: $breakpoint-md) {
        max-width: 220px;
      }
    }

    .intro {
      @include flex(column, start, start);
      @include text-sm;

      @media screen and (min-width: $breakpoint-xs) {
        gap: 4px;
      }

      .name {
        @include text-lg;

        font-weight: 600;
      }

      .rating {
        font-weight: 700;

        img {
          vertical-align: text-bottom;
          margin-right: 4px;
        }
      }
    }
  }

  .right {
    @include flex(column, start, start, $gap: 18px);

    @media screen and (min-width: $breakpoint-md) {
      gap: 28px;
    }

    .column {
      @include flex(column, start, stretch, $gap: 15px);

      .header {
        @include flex(row, space-between, center, $gap: 5px);
        @include text-lg;

        font-weight: 700;

        @media screen and (min-width: $breakpoint-sm) {
          @include text-xl;
        }
      }

      .perks {
        @include flex(row, start, stretch, wrap, 12px 18px);
        @include text-sm;

        @media screen and (min-width: $breakpoint-sm) {
          @include text-md;
        }
      }
    }
  }
}
</style>
